/**
 * Sprint-104d-full — Customer order tracking detail page.
 *
 * Route: /dashboard/orders/:trackingId
 *
 * Composition (top → bottom):
 *   • Header: back button + status badge + tracking_id + refreshed-at
 *   • LiveTrackingMap (pickup + drop pins + polyline)
 *   • TrackingSummaryCards (ETA / status / distance / parcels)
 *   • ShipmentTimeline (8-stage timeline driven by status_timeline)
 *   • Package card + Address cards
 *   • Agent card (when assigned)
 *   • PaymentOverviewCard (method/paid/due + invoice download)
 *
 * Live updates: useOrderRealtime polls every 15 s (Sprint-104d default).
 * Emits a one-shot audit-log ping on mount (backend rate-limits 1/hr).
 */
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Package, ShieldCheck, MapPin, User, Phone, Bike, Star, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import StatusBadge from "../components/StatusBadge";
import useOrderRealtime from "../hooks/useOrderRealtime";
import useOrderAgentPin from "../hooks/useOrderAgentPin";
import useLifecycleToasts from "../components/notifications/NotificationToast";
import LiveTrackingMap from "../components/tracking/LiveTrackingMap";
import TrackingSummaryCards from "../components/tracking/TrackingSummaryCards";
import ShipmentTimeline from "../components/tracking/ShipmentTimeline";
import PaymentOverviewCard from "../components/tracking/PaymentOverviewCard";

function SkeletonView() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8 animate-pulse" data-testid="track-detail-skeleton">
      <div className="h-9 w-32 rounded-lg bg-slate-200 mb-6" />
      <div className="h-7 w-64 rounded-lg bg-slate-200 mb-6" />
      <div className="h-[280px] sm:h-[360px] rounded-2xl bg-slate-100 mb-5" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[0,1,2,3].map((i) => <div key={i} className="h-20 rounded-2xl bg-slate-100" />)}
      </div>
      <div className="h-72 rounded-2xl bg-slate-100 mb-5" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="h-32 rounded-2xl bg-slate-100" />
        <div className="h-32 rounded-2xl bg-slate-100" />
      </div>
    </div>
  );
}

function ErrorView({ kind, onBack, onRetry }) {
  const cfg = {
    "404":   { title: "Order not found",      body: "We couldn't find an order with that tracking ID.", Icon: Package },
    "403":   { title: "Access denied",        body: "You don't have access to this order.",             Icon: ShieldCheck },
    "error": { title: "Couldn't load this order", body: "Something went wrong. Try again in a moment.", Icon: AlertTriangle },
  }[kind] || { title: "Couldn't load this order", body: "Something went wrong.", Icon: AlertTriangle };
  const { Icon } = cfg;
  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid={`track-detail-error-${kind}`}>
      <div className="w-14 h-14 mx-auto rounded-full bg-rose-50 flex items-center justify-center">
        <Icon className="h-7 w-7 text-rose-600" />
      </div>
      <h2 className="font-['Outfit'] text-xl font-bold text-slate-900 mt-4">{cfg.title}</h2>
      <p className="text-sm text-slate-500 mt-1">{cfg.body}</p>
      <div className="flex items-center justify-center gap-3 mt-6">
        <Button variant="outline" onClick={onBack} data-testid="track-detail-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to orders
        </Button>
        {kind === "error" && (
          <Button onClick={onRetry} className="bg-[#FF6B00] hover:bg-orange-600 text-white" data-testid="track-detail-retry-btn">
            Try again
          </Button>
        )}
      </div>
    </div>
  );
}

function formatLastUpdated(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" });
  } catch {
    return "—";
  }
}

export default function OrderTrackingDetail() {
  const { trackingId } = useParams();
  const navigate = useNavigate();
  const { order, eta, loading, errorKind, refreshedAt, refresh } = useOrderRealtime(trackingId);
  // Phase 105-Agent-C — poll the assigned agent's live position every
  // 15s while the order is still in an agent-active status.
  const agentPin = useOrderAgentPin(trackingId, order?.status);
  // And emit a toast on each notable status flip (uses sonner).
  useLifecycleToasts(order);

  const back = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/dashboard/orders");
  };

  if (loading) return <SkeletonView />;
  if (errorKind) return <ErrorView kind={errorKind} onBack={back} onRetry={refresh} />;

  const o = order;
  // Sprint-104d-full: coords live in `pickup_location.lat/lng` and
  // `delivery_location.lat/lng` in the legacy schema. Phase-104 orders
  // may use top-level `pickup_lat/lng`. Read both.
  const pickupLat = o?.pickup_lat ?? o?.pickup_location?.lat;
  const pickupLng = o?.pickup_lng ?? o?.pickup_location?.lng;
  const dropLat = o?.delivery_lat ?? o?.delivery_location?.lat;
  const dropLng = o?.delivery_lng ?? o?.delivery_location?.lng;
  const pickup = (pickupLat != null && pickupLng != null) ? { lat: Number(pickupLat), lng: Number(pickupLng) } : null;
  const delivery = (dropLat != null && dropLng != null) ? { lat: Number(dropLat), lng: Number(dropLng) } : null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8 space-y-5" data-testid="track-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3" data-testid="track-detail-header">
        <button
          type="button"
          onClick={back}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[#FF6B00] transition"
          data-testid="track-detail-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={refresh}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            title={`Last refreshed ${formatLastUpdated(refreshedAt)}`}
            data-testid="track-detail-refresh-btn"
          >
            <RefreshCw className="h-3 w-3" /> {formatLastUpdated(refreshedAt)}
          </button>
          <StatusBadge status={o.status} />
        </div>
      </div>

      <div>
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900" data-testid="track-detail-tracking-id">
          Order <span className="font-mono text-[#FF6B00]">{o.tracking_id}</span>
        </h1>
        <p className="text-sm text-slate-500 mt-1">Tracking your shipment in real-time. This page refreshes every 15 seconds.</p>
      </div>

      {/* Map */}
      <LiveTrackingMap pickup={pickup} delivery={delivery} agent={agentPin} />

      {/* Summary cards */}
      <TrackingSummaryCards order={o} eta={eta} />

      {/* Timeline */}
      <ShipmentTimeline order={o} />

      {/* Package details */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6" data-testid="track-detail-package">
        <h2 className="font-['Outfit'] text-base font-bold text-slate-900 mb-3 flex items-center gap-2">
          <Package className="h-4 w-4 text-[#FF6B00]" /> Package
        </h2>
        <div className="flex gap-4">
          {o.package_photo_url && (
            <img
              src={o.package_photo_url}
              alt="Package"
              className="w-20 h-20 rounded-xl object-cover border border-slate-100"
              data-testid="track-detail-package-photo"
            />
          )}
          <div className="flex-1 min-w-0 space-y-1.5 text-sm">
            <p className="text-slate-900 font-medium">{o.package_description || "Package"}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
              {o.package_weight && <span><b className="text-slate-700">{o.package_weight}</b> kg</span>}
              {(o.length_cm || o.width_cm || o.height_cm) && (
                <span>
                  <b className="text-slate-700">{o.length_cm || "?"} × {o.width_cm || "?"} × {o.height_cm || "?"}</b> cm
                </span>
              )}
              {o.item_size && <span className="capitalize"><b className="text-slate-700">{o.item_size}</b> size</span>}
              {o.service_type && <span className="capitalize"><b className="text-slate-700">{String(o.service_type).replace(/_/g, " ")}</b></span>}
            </div>
          </div>
        </div>
      </div>

      {/* Addresses */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="track-detail-pickup">
          <p className="text-[11px] uppercase font-bold tracking-wider text-emerald-600 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> Pickup
          </p>
          <p className="text-sm text-slate-900 mt-1.5 leading-relaxed">{o.pickup_address || "—"}</p>
          {o.pickup_contact_name && (
            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
              <User className="h-3 w-3" /> {o.pickup_contact_name}
            </p>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="track-detail-delivery">
          <p className="text-[11px] uppercase font-bold tracking-wider text-orange-600 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> Delivery
          </p>
          <p className="text-sm text-slate-900 mt-1.5 leading-relaxed">{o.delivery_address || "—"}</p>
          {o.delivery_contact_name && (
            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
              <User className="h-3 w-3" /> {o.delivery_contact_name}
            </p>
          )}
        </div>
      </div>

      {/* Agent */}
      {o.agent_name && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="track-detail-agent">
          <h2 className="font-['Outfit'] text-base font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Bike className="h-4 w-4 text-[#FF6B00]" /> Your delivery agent
          </h2>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold">
              {(o.agent_name || "A").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{o.agent_name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                {o.agent_rating && (
                  <span className="text-xs text-slate-500 flex items-center gap-0.5">
                    <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {o.agent_rating.toFixed(1)}
                  </span>
                )}
                {o.agent_vehicle && (
                  <span className="text-xs text-slate-500 capitalize">· {o.agent_vehicle}</span>
                )}
              </div>
            </div>
            {o.agent_phone && (
              <a
                href={`tel:${o.agent_phone}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-semibold hover:bg-emerald-100"
                data-testid="track-detail-call-agent"
              >
                <Phone className="h-3.5 w-3.5" /> Call
              </a>
            )}
          </div>
        </div>
      )}

      {/* Payment + invoice download */}
      <PaymentOverviewCard order={o} trackingId={trackingId} />

      {/* Contact support */}
      <div className="text-center pt-2">
        <Link
          to="/dashboard/contact-us"
          state={{ orderId: o.tracking_id }}
          className="text-sm text-[#FF6B00] hover:underline font-semibold"
          data-testid="track-detail-help-link"
        >
          Need help with this order? Contact support →
        </Link>
      </div>
    </div>
  );
}
